import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Wordmark } from "@/components/Brand";

const EMAIL = "privacy@driveassure.systems";
const Mail = () => <a href={`mailto:${EMAIL}`} className="text-[#1FA097] font-medium">{EMAIL}</a>;

const Section = ({ n, title, children }) => (
  <section className="mt-9">
    <h2 className="font-display font-bold text-xl text-[#2B2B2B]">{n}. {title}</h2>
    <div className="mt-3 space-y-3 text-[#2B2B2B]/70 leading-relaxed">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2 pl-1">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#2BBFB3] shrink-0" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-policy-page" className="min-h-screen bg-white text-[#2B2B2B]">
      <header className="border-b border-[#E5E5E5]">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" data-testid="legal-logo-home"><Wordmark /></Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#2B2B2B]/60 hover:text-[#2B2B2B] transition-colors"
          >
            <ArrowLeft size={16} /> Back to site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-16 lg:py-24">
        <p className="overline text-[#2BBFB3]">Legal</p>
        <h1 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl">Privacy Policy</h1>
        <p className="mt-4 text-[#2B2B2B]/50">Last updated: October 2026</p>

        <Section n={1} title="Who we are">
          <p>
            DriveAssure is operated by James Sadler ABN 76 728 192 915. We are committed to protecting the
            privacy of all users in accordance with applicable privacy laws including the Australian Privacy
            Act 1988, the EU General Data Protection Regulation (GDPR), and equivalent data protection
            legislation across other jurisdictions.
          </p>
        </Section>

        <Section n={2} title="What data we collect">
          <Bullets items={[
            "Name and email address upon registration",
            "Driving licence number",
            "GPS location data during active lessons",
            "Lesson records including times, durations, locations, and assessment notes",
            "Device information and app usage data",
          ]} />
        </Section>

        <Section n={3} title="Why we collect it">
          <Bullets items={[
            "To provide lesson tracking, assessment, and progress reporting",
            "To facilitate progress sharing between instructors, learners, and supervisors",
            "To generate AI-powered practice plans",
            "To process subscription payments securely via Stripe",
          ]} />
          <p>
            We may use anonymised, non-identifiable data for research and development purposes, including
            improving the accuracy of AI-generated assessments and practice recommendations. No personally
            identifiable information is used for this purpose.
          </p>
        </Section>

        <Section n={4} title="Who we share it with">
          <p>
            We do not sell your data. Data is shared only with linked instructors, learners, and supervisors
            within your DriveAssure network, with Stripe for payment processing, and with cloud infrastructure
            providers who host the platform under strict data agreements.
          </p>
        </Section>

        <Section n={5} title="GPS and location data">
          <p>
            Location data is collected only during active lessons with your consent. It is stored securely and
            accessible only to the learner, their linked instructor, and approved supervisors.
          </p>
        </Section>

        <Section n={6} title="Data retention">
          <p>
            Your data is retained for as long as your account is active. You may request deletion at any time
            by emailing <Mail />
          </p>
        </Section>

        <Section n={7} title="Your rights">
          <p>
            You have the right to access, correct, or delete your personal data at any time. Contact us at <Mail />
          </p>
        </Section>

        <Section n={8} title="Your rights (EU users)">
          <p>
            You have the right to access, correct, or delete your personal data at any time. Users in the
            European Union may also exercise their rights under GDPR including the right to data portability
            and the right to lodge a complaint with a supervisory authority.
          </p>
        </Section>

        <Section n={9} title="Contact">
          <p>For any privacy enquiries: <Mail /></p>
        </Section>
      </main>
    </div>
  );
}
